import { cx } from '@emotion/css';
import { type FC, useContext, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { PrimitivesContext } from '../Primitives';
import {
  backIconCss,
  breadcrumbActiveCss,
  breadcrumbAnchorCss,
  breadcrumbContainerCss,
  breadcrumbCss,
  breadcrumbTextCss,
  tooltipCss,
} from './Breadcrumb.styles';

export type BreadcrumbProps = {
  breadcrumbText: string;
  slug?: string;
  isCurrent?: boolean;
  showBackIcon?: boolean;
};

export const Breadcrumb: FC<BreadcrumbProps> = ({
  breadcrumbText,
  slug,
  isCurrent,
  showBackIcon,
}) => {
  const { Anchor } = useContext(PrimitivesContext);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkIfTruncated = () => {
    if (!textRef.current) return;

    const { offsetWidth, scrollWidth } = textRef.current;
    setIsTruncated(scrollWidth > offsetWidth);
  };

  const text = (
    <span ref={textRef} className={breadcrumbTextCss}>
      {breadcrumbText}
    </span>
  );

  const breadcrumb =
    slug && !isCurrent ? (
      <Anchor href={slug} className={cx(breadcrumbCss, breadcrumbAnchorCss)}>
        {showBackIcon && <Icon name="chevron-left" size={24} className={backIconCss} />}
        {text}
      </Anchor>
    ) : (
      <span
        className={cx(breadcrumbCss, {
          [breadcrumbActiveCss]: isCurrent,
        })}
      >
        {text}
      </span>
    );

  return (
    <span
      className={breadcrumbContainerCss}
      data-truncated={isTruncated}
      onMouseEnter={checkIfTruncated}
    >
      {breadcrumb}
      <span role="tooltip" className={tooltipCss}>
        {breadcrumbText}
      </span>
    </span>
  );
};

Breadcrumb.displayName = 'BreadcrumbItem';
